import React, { useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { AuthContext } from '../../shared/context/auth-context';
import { useHttpClient } from '../../shared/hooks/http-hook';

interface UseSignupReturn {
  // Screen state
  activeScreen: number | undefined;
  setActiveScreen: (screen: number) => void;

  // Client configuration
  clientId: string | undefined;
  departments: any[] | undefined;
  subDepartments: any[] | undefined;
  regions: any[] | undefined;
  image: any;
  erv: string | boolean | undefined;
  ervFieldName: string;
  fandf: boolean;
  isStandardAuthActive: boolean;
  isMicrosoftAuthActive: boolean;
  isGoogleAuthActive: boolean;
  singleSignOn: any[];
  domainValidation: boolean;
  passcode: string;
  fandFToken: any;
  profile: any;
  name: string | undefined;
  isHub: boolean;
  tempAccessToken: string;

  // User data
  userId: string | undefined;
  setName: (name: string) => void;
  setUserId: (id: string) => void;

  // API handlers
  registerUser: (dataObj: any) => Promise<void>;
  handleEIdcheck: (eid: string) => Promise<void>;
  createProfile: (profile: any) => Promise<void>;
  createPasscode: (passcode: string) => Promise<void>;

  // Navigation
  handleContinue: (dataObj: any) => void;

  // Modal state
  modalIsOpen: boolean;
  modalTextContent: string;
  modalQuestionTitle: string;
  openModal: (params: { modalText: string; questionTitle: string }) => void;
  modalClickHandler: () => void;

  // Error handling
  error: string | undefined;
  isLoading: boolean;

  // Auth context
  auth: any;

  // Focus management
  screenRef: React.RefObject<HTMLDivElement>;
}

export const useSignup = (): UseSignupReturn => {
  const auth = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  const [userId, setUserId] = useState<string | undefined>();
  const [clientId, setClientId] = useState<string | undefined>();
  const [activeScreen, setActiveScreen] = useState<number | undefined>();
  const [departments, setDepartments] = useState();
  const [subDepartments, setSubDepartments] = useState();
  const [regions, setRegions] = useState();
  const [image, setImage] = useState();
  const [erv, setErv] = useState<string | boolean | undefined>(false);
  const [ervFieldName, setErvFieldName] = useState('');
  const [fandf, setFandf] = useState(false);
  const [isStandardAuthActive, setIsStandardAuthActive] = useState(false);
  const [isMicrosoftAuthActive, setIsMicrosoftAuthActive] = useState(false);
  const [isGoogleAuthActive, setIsGoogleAuthActive] = useState(false);
  const [singleSignOn, setSingleSignOn] = useState([]);
  const [domainValidation, setDomainValidation] = useState(false);
  const [passcode, setPasscode] = useState('');
  const [fandFToken, setFandFToken] = useState();
  const [profile, setProfile] = useState();
  const [name, setName] = useState<string | undefined>();
  const [isHub, setIsHub] = useState(false);
  const [tempAccessToken, setTempAccessToken] = useState('');

  // Modal state
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalTextContent, setModalTextContent] = useState('');
  const [modalQuestionTitle, setModalQuestionTitle] = useState('');

  // Focus management
  const screenRef = useRef<HTMLDivElement>(null);

  const { error, isLoading, sendRequest } = useHttpClient();

  // Initialize client data from localStorage
  useEffect(() => {
    const storedClientData = JSON.parse(localStorage.getItem('userData') || '{}');

    if (!auth.isLoggedIn && !location.state) {
      // Return to company code page
      if (!storedClientData || !storedClientData.clientid) {
        navigate('/');
        return;
      }

      // Retrieve stored data from local storage
      if (!clientId) {
        setClientId(storedClientData.clientid);
      }
      if (storedClientData.fandfUser) {
        setFandf(true);
        setFandFToken(storedClientData.fandfUser);
      }
      if (storedClientData.domain_validation) {
        setDomainValidation(true);
      }
      setIsStandardAuthActive(storedClientData.isStandardAuthActive);
      setIsMicrosoftAuthActive(storedClientData.isMicrosoftAuthActive);
      setIsGoogleAuthActive(storedClientData.isGoogleAuthActive);
      setSingleSignOn(storedClientData.singleSignOn);
      setActiveScreen(1);
    } else if (location.state && location.state.user?._id) {
      // Handle user data from company code or sign in page
      const { user, dept, sub_depts, regions, profilePic, erv, erv_field_name, tempToken, hub } =
        location.state;

      setUserId(user._id);
      setName(user.name);

      setFandf(user.access === '2');
      if (hub) {
        setIsHub(true);
      }

      if (
        user._id &&
        user.name &&
        !user.passcode &&
        !user.completed_registration &&
        hub &&
        user.access === '4'
      ) {
        // Send hub user lvl4 to accept T&C and set passcode
        setProfile(user);
        setTempAccessToken(tempToken);
        setActiveScreen(3);
      } else if (
        user._id &&
        user.name &&
        !user.passcode &&
        (user.auth_type === undefined || user.auth_type === 'ChampionHealth')
      ) {
        // Send user to set passcode screen
        setTempAccessToken(tempToken);
        setActiveScreen(4);
      } else if (
        (user._id && !user.name) ||
        user.completed_registration !== undefined ||
        user.completed_registration !== true
      ) {
        // Send user to complete their profile screen
        setDepartments(dept?.map((value, index) => ({ id: index + 1, title: value })));
        setSubDepartments(sub_depts);
        setRegions(regions);
        setImage(profilePic);
        setErv(erv);
        setErvFieldName(erv_field_name);
        setActiveScreen(2);
      }
    }
  }, [auth.isLoggedIn, clientId, location, navigate]);

  // Focus management when screen changes
  useEffect(() => {
    if (activeScreen && screenRef.current) {
      // Find the first focusable element within the screen
      const focusableElements = screenRef.current.querySelectorAll(
        'input:not([disabled]), button:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
      );
      if (focusableElements.length > 0) {
        (focusableElements[0] as HTMLElement).focus();
      }
    }
  }, [activeScreen]);

  // Handle logged in user redirection
  useEffect(() => {
    if (auth.isLoggedIn) {
      // Check if assessment complete or redirected from another page
      const storedUrl = sessionStorage.getItem('DirectLink');
      sessionStorage.removeItem('DirectLink');

      if (storedUrl && auth.tags && auth.tags.length > 0) {
        navigate(storedUrl);
      } else if (auth.tags && auth.tags.length > 0) {
        navigate('/for-you');
      } else if ((auth.tags && auth.tags.length < 1) || !auth.tags) {
        navigate('/assessment');
      }
    }
  }, [auth.isLoggedIn, auth.tags, navigate]);

  const handleRegisterUserResponse = async (response: any) => {
    if (response) {
      if (response.user.name !== '' && response.user.name !== null) {
        setName(response.user.name);
      }
      if (response?.hub) {
        setIsHub(true);
      }
      setUserId(response.user._id);
      setDepartments(response.dept?.map((value, index) => ({ id: index + 1, title: value })));
      setSubDepartments(response.sub_depts);
      setRegions(response.regions);
      setImage(response.profilePic);
      setErv(response.erv);
      setErvFieldName(response.erv_field_name);
      setActiveScreen(2);
    }
  };

  const registerUser = async (dataObj: any) => {
    try {
      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/users/signup`,
        'POST',
        JSON.stringify({
          email: dataObj.email.toLowerCase(),
          password: dataObj.password,
          fandfUser: fandFToken,
          clientId,
          platform: 'web',
        })
      );
      handleRegisterUserResponse(response);
    } catch (err) {
      // Error is handled by useHttpClient
    }
  };

  const handleEIdcheck = async (eid: string) => {
    try {
      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/users/checkEId`,
        'POST',
        JSON.stringify({
          userid: userId,
          eid,
        })
      );
      if (response && response.isValidEId === true) {
        setActiveScreen(3);
      }
    } catch (err) {
      // Error is handled by useHttpClient
    }
  };

  const handleAuthenticatedUser = async (response: any) => {
    if (response && response.user) {
      auth.login(
        response.user._id,
        response.token,
        response.user.name,
        response?.token?.expirationDate,
        response.user.access,
        response.user.image,
        response.user.tags,
        response.clientName,
        response.user.nps_status,
        response.user.auth_type,
        response.user.email,
        response.user.language,
        response?.hub
      );
      if (response.user.tags.length > 0) {
        navigate('/for-you');
      } else {
        navigate('/assessment');
      }
    }
  };

  const createProfile = async (profileData: any) => {
    try {
      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/users/createProfile`,
        'POST',
        JSON.stringify({
          userId,
          name: profileData.name,
          dept: profileData.departmentid,
          sub_dept: profileData.subDepartmentid,
          regionid: profileData.regionid,
          image,
          eid: profileData.eid,
          platform: 'web',
        })
      );

      if (response) {
        if (response.token && response.user && response.clientName) {
          handleAuthenticatedUser(response);
        } else {
          setTempAccessToken(response.tempToken);
          setActiveScreen(4);
        }
      }
    } catch (err) {
      // Error is handled by useHttpClient
    }
  };

  const createPasscode = async (passcodeData: string) => {
    try {
      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/users/setPasscode`,
        'POST',
        JSON.stringify({
          tempToken: tempAccessToken,
          passcode: passcodeData,
          platform: 'web',
        })
      );
      handleAuthenticatedUser(response);
    } catch (err) {
      // Error is handled by useHttpClient
    }
  };

  const handleContinue = useCallback(
    (dataObj: any) => {
      if (!dataObj?.progress) {
        setActiveScreen(1);
      } else if (dataObj.progress === 'screen1') {
        registerUser(dataObj);
      } else if (dataObj.progress === 'screen2') {
        setProfile(dataObj);
        if (erv === 'yes' && !fandf && !isHub) {
          handleEIdcheck(dataObj.eid);
        } else {
          setActiveScreen(3);
        }
      } else if (dataObj.progress === 'screen3' && dataObj.accepted) {
        createProfile(profile);
      } else if (dataObj.progress === 'screen4') {
        setPasscode(dataObj.code);
        setActiveScreen(5);
      } else if (dataObj.progress === 'screen5' && dataObj.code === passcode) {
        createPasscode(passcode);
      } else if (dataObj.progress === 'screen5' && dataObj.code === 'reset') {
        setActiveScreen(4);
      }
    },
    [
      erv,
      fandf,
      isHub,
      profile,
      passcode,
      registerUser,
      handleEIdcheck,
      createProfile,
      createPasscode,
    ]
  );

  const modalClickHandler = useCallback(() => {
    setModalIsOpen(false);
  }, []);

  const openModal = useCallback(
    ({ modalText, questionTitle }: { modalText: string; questionTitle: string }) => {
      setModalIsOpen(true);
      setModalTextContent(modalText);
      setModalQuestionTitle(questionTitle);
    },
    []
  );

  return {
    // Screen state
    activeScreen,
    setActiveScreen,

    // Client configuration
    clientId,
    departments,
    subDepartments,
    regions,
    image,
    erv,
    ervFieldName,
    fandf,
    isStandardAuthActive,
    isMicrosoftAuthActive,
    isGoogleAuthActive,
    singleSignOn,
    domainValidation,
    passcode,
    fandFToken,
    profile,
    name,
    isHub,
    tempAccessToken,

    // User data
    userId,
    setName,
    setUserId,

    // API handlers
    registerUser,
    handleEIdcheck,
    createProfile,
    createPasscode,

    // Navigation
    handleContinue,

    // Modal state
    modalIsOpen,
    modalTextContent,
    modalQuestionTitle,
    openModal,
    modalClickHandler,

    // Error handling
    error,
    isLoading,

    // Auth context
    auth,

    // Focus management
    screenRef,
  };
};
